import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { fetchPlayerBranding, resetPlayerBrandingRequestsForTests } from "../lib/playerBranding";
import Icon from "./icons";

interface PlayerTournamentBottomNavProps {
  tournamentId: number | string;
  scheduleLabel?: string;
  /** «Este torneo usa credencial» cuando la página ya trae la tarjeta del
   * torneo (Inicio, Credencial): true/false lo fija sin pedir /branding;
   * null = la página aún lo está cargando (la barra espera, no lo pide).
   * Sin la prop, la barra lo pide por su cuenta. */
  credentialEnabled?: boolean | null;
}

// "Matchups" salió del nav (redundante con Calendario + Draw); su ruta
// sigue viva para deep-links. Eduardo 2026-09-24: las pestañas SIEMPRE se
// ven; mientras el admin no termina el wizard, cada sección muestra su aviso
// ("Cuando el organizador publique el Draw, aparecerá en esta sección").
// Eduardo 2026-10-05 (maqueta aprobada): en celular «Comunicados» se lee
// «Avisos» (cabe en la barra de abajo); en computadora sigue «Comunicados».
const tournamentNavItems: Array<{ label: string; shortLabel?: string; icon: string; path: string }> = [
  { label: "Calendario", icon: "calendar", path: "schedule" },
  { label: "Draw", icon: "bracket", path: "bracket" },
  { label: "Ranking", icon: "chart", path: "groups" },
  { label: "Credencial", icon: "ticket", path: "credential" },
  { label: "Jugadores", icon: "users", path: "players" },
  { label: "Comunicados", shortLabel: "Avisos", icon: "megaphone", path: "communications" },
  { label: "Reglamento", icon: "book", path: "rules" },
];

// «Este torneo usa credencial» (Eduardo 2026-10-05): sin credencial no hay
// pestaña «Credencial». Se recuerda por torneo mientras la app está abierta:
// la barra sale en todas las vistas del jugador y no lo pide a cada pantalla.
const credentialEnabledByTournament = new Map<number, boolean>();

export function resetTournamentCredentialCacheForTests() {
  credentialEnabledByTournament.clear();
  resetPlayerBrandingRequestsForTests();
}

/** true/false, o null mientras no se sabe: entonces la pestaña no se pinta
 * (antes salía un momento y desaparecía en los torneos sin credencial). */
function useTournamentUsesCredential(
  tournamentId: number | string,
  fromPage: boolean | null | undefined,
): boolean | null {
  const { token } = useAuth();
  const id = Number(tournamentId);
  const canAsk = Boolean(token) && Number.isFinite(id) && id > 0;
  const [fetched, setFetched] = useState<{ id: number; value: boolean } | null>(null);

  useEffect(() => {
    if (typeof fromPage === "boolean") {
      credentialEnabledByTournament.set(id, fromPage);
      return;
    }
    // null: la página ya lo trae con su tarjeta; no se pide dos veces.
    if (fromPage === null || !token || !canAsk || credentialEnabledByTournament.has(id)) return;
    let cancelled = false;
    // Misma petición que los espacios de patrocinador (una sola por pantalla).
    fetchPlayerBranding(token, id)
      .then((response) => {
        // Sin el dato (servidor anterior) = sí usa credencial.
        const value = response.data.credential_enabled !== false;
        credentialEnabledByTournament.set(id, value);
        if (!cancelled) setFetched({ id, value });
      })
      .catch(() => {
        // Sin respuesta la pestaña se queda (sin recordarlo): la pantalla de
        // la credencial explica si el torneo no la usa.
        if (!cancelled) setFetched({ id, value: true });
      });
    return () => {
      cancelled = true;
    };
  }, [canAsk, fromPage, id, token]);

  if (typeof fromPage === "boolean") return fromPage;
  const known = credentialEnabledByTournament.get(id);
  if (known !== undefined) return known;
  if (fetched && fetched.id === id) return fetched.value;
  // Sin forma de preguntar, como siempre: con pestaña.
  return canAsk ? null : true;
}

export default function PlayerTournamentBottomNav({
  tournamentId,
  scheduleLabel,
  credentialEnabled,
}: PlayerTournamentBottomNavProps) {
  const location = useLocation();
  const usesCredential = useTournamentUsesCredential(tournamentId, credentialEnabled);
  const base = `/player/tournaments/${tournamentId}`;
  const path = location.pathname;

  const startsWith = (target: string) =>
    path === target || path.startsWith(`${target}/`);

  const linkClass = (active: boolean, extra = "") =>
    `player-tournament-bottom-nav__link${active ? " is-active" : ""}${extra}`;

  const items = usesCredential === true
    ? tournamentNavItems
    : tournamentNavItems.filter((item) => item.path !== "credential");
  const homeActive = path === base || path === `${base}/`;

  // Eduardo 2026-10-05 (maqueta aprobada): en celular y tableta (< 1024 px)
  // es la barra flotante de abajo con íconos; en computadora, una fila de
  // pestañas arriba del contenido con «Inicio» al principio. Mismo <nav>
  // en todas las vistas del jugador: el acomodo lo decide styles.css.
  return (
    <nav
      className="player-tournament-bottom-nav"
      aria-label="Vista jugador del torneo"
      // Tantas columnas como pestañas de la barra de abajo («Inicio» solo
      // sale en computadora): sin «Credencial» son 6, sin hueco a la derecha.
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      <Link
        to={base}
        className={linkClass(homeActive, " player-tournament-bottom-nav__link--desktop-only")}
        aria-current={homeActive ? "page" : undefined}
      >
        <span className="player-tournament-bottom-nav__icon" aria-hidden="true">
          <Icon name="home" />
        </span>
        <small>Inicio</small>
      </Link>
      {items.map((item) => {
        const to = `${base}/${item.path}`;
        const active = startsWith(to);
        const label = item.path === "schedule" ? (scheduleLabel ?? item.label) : item.label;
        return (
          <Link key={item.path} to={to} className={linkClass(active)} aria-current={active ? "page" : undefined}>
            <span className="player-tournament-bottom-nav__icon" aria-hidden="true">
              <Icon name={item.icon} />
            </span>
            {item.shortLabel ? (
              <small>
                <span className="player-tournament-bottom-nav__label--mobile">{item.shortLabel}</span>
                <span className="player-tournament-bottom-nav__label--desktop">{label}</span>
              </small>
            ) : (
              <small>{label}</small>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
